import { useState, useEffect } from "react";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import {
	EmployeLeaveTypeSummary,
	LeaveTypeDto,
	ApplicableLeaveTypeOrderBy,
} from "../../../../models/leave-type.dto";
import { useGetEmployeeLeaveTypeSummary } from "../../../../services/leave-request.service";
import { useGetApplicableLeaveType } from "../../../../services/leave-type.service";
import { useSearchParams } from "react-router-dom";

/**
 * Custom hook for managing leave type summaries and related functionalities
 *
 * This hook handles the state and operations related to leave types, summaries, and menu interactions
 * for an employee's leave management dashboard.
 *
 * @param employeeId - The ID of the employee whose leave summary is being managed
 * @returns {Object} An object containing:
 *  - leaveTypes: Array of available leave types for the employee
 *  - leaveTypeSummary: Current selected leave type summary details
 *  - activeLeaveType: Currently selected leave type
 *  - dropdownOpen: Boolean state for dropdown menu visibility
 *  - setDropdownOpen: Function to toggle dropdown menu state
 *  - handleLeaveTypeMenu: Function to handle leave type selection and menu interaction
 */
export function useManageLeaveSummary(employeeId: number) {
	const [searchParams] = useSearchParams();
	const refreshSummary = searchParams.get("refreshSummary");
	const [leaveTypeSummary, setLeaveTypeSummary] =
		useState<EmployeLeaveTypeSummary>({} as EmployeLeaveTypeSummary);
	const [activeLeaveType, setActiveLeaveType] = useState<LeaveTypeDto>();
	const [dropdownOpen, setDropdownOpen] = useState(false);

	const { data: leaveTypes, isLoading } = useGetApplicableLeaveType({
		employeeId,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: ApplicableLeaveTypeOrderBy.CREATED_AT_DESC,
	});

	const { mutate: getLeaveTypeSummary } = useGetEmployeeLeaveTypeSummary();

	const handleLeaveTypeSummary = (leaveTypeId: number) => {
		getLeaveTypeSummary(
			{ employeeId, leaveTypeId },
			{ onSuccess: (data) => setLeaveTypeSummary(data.data) },
		);
	};

	useEffect(() => {
		if (!isLoading && leaveTypes[0]) {
			handleLeaveTypeSummary(leaveTypes[0].id);
			setActiveLeaveType(leaveTypes[0]);
		}
	}, [isLoading]);

	useEffect(() => {
		if (refreshSummary && activeLeaveType) {
			handleLeaveTypeSummary(activeLeaveType.id);
		}
	}, [refreshSummary]);

	const handleLeaveTypeMenu = (leaveType: LeaveTypeDto) => {
		handleLeaveTypeSummary(leaveType.id);
		setActiveLeaveType(leaveType);
		setDropdownOpen((prev) => !prev);
	};

	return {
		leaveTypes,
		leaveTypeSummary,
		activeLeaveType,
		dropdownOpen,
		setDropdownOpen,
		handleLeaveTypeMenu,
	};
}
